import React, { PropsWithChildren } from 'react';
import { cn } from '@lib/utils';
import CapsuleButton, { CapsuleButtonProps } from '@/components/button';

interface Props extends PropsWithChildren {
  className?: string;
}

export const OuterWrapper = ({ className, children }: Props) => {
  const baseClassNames =
    'flex flex-[1_0_0] flex-col items-center gap-8 bg-3b-blue-light-900 px-8 py-[100px] md:flex-row md:justify-between md:px-8 md:py-[150px]';
  return (
    <section className={cn(baseClassNames, className)}>{children}</section>
  );
};

export const InnerWrapper = ({ className, children }: Props) => {
  const baseClassNames =
    'flex flex-col gap-y-8 md:mx-auto md:flex-1 md:flex-row md:items-center md:justify-between 2xl:my-0 2xl:max-w-[960px]';
  return <div className={cn(baseClassNames, className)}>{children}</div>;
};

export const Wrapper = ({ children }: Props) => (
  <OuterWrapper>
    <InnerWrapper>{children}</InnerWrapper>
  </OuterWrapper>
);

export const Contents = ({ className, children }: Props) => {
  const baseClassNames =
    'color-3b-blue-light-900 text-center text-[24px] font-bold leading-[32px] text-white md:text-left lg:text-[36px] lg:leading-[44px]';
  return <h2 className={cn(baseClassNames, className)}>{children}</h2>;
};

export const Details = ({
  className,
  ...props
}: React.HtmlHTMLAttributes<HTMLDivElement>) => {
  const baseClassName =
    'text-[16px] font-medium leading-[26px] tracking-[-0.176px]';

  return <div {...props} className={cn(baseClassName, className)} />;
};

export const Description = ({ className, children }: Props) => {
  const baseClassNames =
    'w-[20ch] mx-auto md:w-full text-center text-3b-text-contrast font-3b-body1 md:text-left font-medium';
  return <h5 className={cn(baseClassNames, className)}>{children}</h5>;
};

export const ButtonWrapper = ({ className, children }: Props) => {
  const baseClassNames =
    'flex flex-row flex-wrap items-center justify-center md:justify-end gap-[1rem] 2xl:flex-row';
  return <div className={cn(baseClassNames, className)}>{children}</div>;
};

const commonClassNames =
  'cta-button px-[20px] py-[10px] text-sm font-bold transition-all duration-200 ease-linear hover:bg-3b-red-light-600 focus:bg-3b-red-light-600 active:bg-3b-red-light-600 hover:text-white  focus:text-white  active:text-white lg:min-w-[215px] lg:px-[40px] lg:py-[15px] lg:text-base 2xl:min-w-max';

const variantClassNames = {
  primary: '',
  secondary: '',
  outlined: 'border-solid border-white text-white bg-transparent',
  transparent: '',
  accent: '',
} as const;

export const CTAButton = ({
  children,
  variant = 'secondary',
  className,
  ...props
}: CapsuleButtonProps) => {
  return (
    <CapsuleButton
      size="large"
      variant={variant}
      className={cn(
        commonClassNames,
        variant ? variantClassNames[variant] : '',
        className,
      )}
      {...props}
    >
      {children}
    </CapsuleButton>
  );
};
